import React, { useState, useEffect } from 'react';
import { StepContainer, StepNavigation } from '../components';
import { StepProps, useFormContext, clearFormFromSession } from '../context';
import { submitFormulario, validateFormData } from '../../../services/formularioService';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

interface FormError {
    field: string;
    message: string;
}

export const Step22_RegraFinal: React.FC<StepProps> = ({ onNext, onPrevious, isLoading }) => {
    const { data } = useFormContext();
    const [countdown, setCountdown] = useState(3);
    const [canContinue, setCanContinue] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [errorList, setErrorList] = useState<FormError[]>([]);

    useEffect(() => {
        if (countdown === 0) {
            setCanContinue(true);
            return;
        }

        const timer = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [countdown]);

    const handleSubmit = async () => {
        setSubmitStatus('loading');
        setErrorMessage('');
        setErrorList([]);

        const validationResult = validateFormData(data);

        if (!validationResult.valid) {
            setSubmitStatus('error');
            setErrorMessage('Por favor, corrija os erros abaixo:');
            setErrorList(validationResult.errors.map(err => ({ field: '', message: err })));
            return;
        }

        try {
            console.log('Submitting form data:', data);

            // Mapeamento de valores amigáveis para escolhas
            const mapSelectValue = (type: string, value: string): string => {
                const selectMaps: Record<string, Record<string, string>> = {
                    xizinhaInterest: {
                        'very-interested': 'Muito interessada — quero aprender o app para facilitar minhas vendas.',
                        'interested': 'Interessada — usaria o app com orientação ou suporte inicial.',
                        'not-interested': 'Pouco interessada — prefiro fazer as vendas de forma mais tradicional (mensagens, catálogo, anotações de vendas em caderneta, etc.).',
                    },
                    appLearning: {
                        'learn-quickly': 'Aprenderia sozinha rapidamente.',
                        'need-help': 'Precisaria de ajuda inicial, mas aprenderia com o tempo.',
                        'need-support': 'Teria dificuldade e preferiria apoio mais próximo.',
                    },
                    technologyConfidence: {
                        'very-confident': 'Muito confiante — adoro mexer com apps e redes!',
                        'confident': 'Confiante — uso bem, mas posso melhorar com orientações.',
                        'learning': 'Ainda não tenho muita prática, mas quero aprender.',
                        'not-interested': 'Não tenho prática e não tenho interesse em aprender.',
                    },
                    residence:{
                        'own': 'Própria',
                        'family': 'Familiar',
                        'rented': 'Alugada',
                    },
                    residenceProof: {
                        'own-name': 'Sim, em meu nome',
                        'spouse-name': 'Sim, no nome do cônjuge',
                        'family-name': 'Sim, no nome de familiar (pai/mãe)',
                        'no-proof': 'Não possuo comprovante no momento',
                    },
                    veiculo: {
                        'car': 'Sim, carro próprio',
                        'motorcycle': 'Sim, moto própria',
                        'public-transport': 'Não possuo veículo, uso transporte público',
                        'frequent-access': 'Não possuo veículo, mas tenho acesso frequente a um',
                    },
                    contractAgreement: {
                        'agree-aware': 'Sim, concordo e estou ciente das regras',
                        'agree-read-first': 'Sim, concordo e gostaria de ler com atenção antes de assinar',
                        'disagree': 'Não concordo',
                    },
                    maritalStatus: {
                        'single': 'Solteira',
                        'married': 'Casada',
                        'divorced': 'Divorciada',
                        'widowed': 'Viúva',
                        'stable-union': 'União Estável',

                    },
                    scholarLevel: {
                        'fundamental-incomplete': 'Fundamental Incompleto',
                        'fundamental-complete': 'Fundamental Completo',
                        'medium-incomplete': 'Médio Incompleto',
                        'medium-complete': 'Médio Completo',
                        'superior-incomplete': 'Superior Incompleto',
                        'superior-complete': 'Superior Completo',
                        'postgraduate': 'Pós-graduação',
                    },
                    disponivelEntrevistaOnline: {
                        'manha': 'Manhã (8h às 12h)',
                        'tarde': 'Tarde (13h às 18h)',
                        'noite': 'Noite (18h às 20h)',
                        'sem-disponibilidade': 'Não tenho disponibilidade',
                    },
                    disponivelTreinamentoVendas: {
                        'disponivel-interessada': 'Sim, tenho disponibilidade e muito interesse',
                        'interesse-limitada': 'Tenho interesse, mas disponibilidade limitada',
                        'disponivel-sem-interesse': 'Tenho disponibilidade, mas não tenho interesse',
                        'sem-disponibilidade': 'Não tenho disponibilidade no momento',
                    },
                };
                return selectMaps[type]?.[value] || value;
            };

            // Mapeamento de dados para o backend
            const mappedData = {
                ...data,
                scholarLevel: mapSelectValue('scholarLevel', data.scholarLevel || ''),
                maritalStatus: mapSelectValue('maritalStatus', data.maritalStatus || ''),
                address: data.address?.trim() + ', ' + 
                        data.addressNumber?.trim() +
                        (data.complement ? ' (' + data.complement.trim() + ')' : ''),
                profitObjective: data.profitGoal?.trim(),
                cpfRestrictions: data.restrictionDetails || '',
                residence: mapSelectValue('residence', data.residence || ''),
                children: data.childrenCount || 'Não',
                doubt: data.doubtsQuestion?.trim(),
                grauInteresse: mapSelectValue('xizinhaInterest', data.xizinhaInterest || ''),
                aprenderAplicativo: mapSelectValue('appLearning', data.appLearning || ''),
                usoTecnologia: mapSelectValue('technologyConfidence', data.technologyConfidence || ''),
                acordoContrato: mapSelectValue('contractAgreement', data.contractAgreement || ''),
                ambassadorReason: data.ambassadorReason?.trim(),
                residenceProof: mapSelectValue('residenceProof', data.residenceProof || ''),
                veiculo: mapSelectValue('veiculo', data.veiculo || ''),
                disponivelEntrevistaOnline: mapSelectValue('disponivelEntrevistaOnline', data.disponivelEntrevistaOnline || ''),
                disponivelTreinamentoVendas: mapSelectValue('disponivelTreinamentoVendas', data.disponivelTreinamentoVendas || ''),
            };

            
            const response = await submitFormulario(mappedData);
            setSubmitStatus('success');

            // Limpar dados da sessão após sucesso
            clearFormFromSession();

            // Redirecionar após 2 segundos
            setTimeout(() => {
                onNext({ ...data });
            }, 2000);

            // setTimeout(() => {
            //   window.open('https://www.instagram.com/xdoinfinito/', '_blank');
            // }, 10000);
        } catch (error) {
            setSubmitStatus('error');
            if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage('Erro ao enviar formulário. Por favor, tente novamente.');
            }
        }
    };

    if (submitStatus === 'success') {
        return (
            <StepContainer title="">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                        <h3 className="font-semibold text-green-900">Formulário enviado com sucesso!</h3>
                        <p className="text-sm text-green-700">Aguarde...</p>
                    </div>
                </div>
            </StepContainer>
        );
    }

    return (
        <StepContainer title="">
            <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-amber-900 text-sm font-semibold mb-2">
                        🚨 IMPORTANTE: clique em finalizar para garantir
                    </p>
                    <p className="text-amber-900 text-sm">
                        que seu pré-cadastro seja concluído com sucesso. Se não finalizar, ele não será validado.
                    </p>
                </div>

                <hr className="my-2" />
                <p className='text-muted-foreground text-xs'>
                    Ao clicar em finalizar, você afirma a veracidade dos dados preenchidos nesse formulário, e está ciente de que seus dados passarão por uma análise de risco para a concessão de crédito, bem como para realizar verificação de cadastro positivo nos principais órgãos de proteção ao crédito e realizar a verificação e confirmação de identidade, e concorda com os termos e condições gerais de nossa política de consignação.
                </p>
                <hr className="my-2" />

                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                    <p className="text-gray-900 text-sm">
                        X do Infinito assegura a conformidade com a Lei Geral de Proteção de Dados (LGPD), Lei nº 13.709/2018, e compromete-se a proteger os dados pessoais presentes nesse formulário.
                    </p>
                </div>

                {/* Error State */}
                {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center gap-4 mb-4">
                            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                            <h3 className="font-semibold text-red-900">{errorMessage}</h3>
                        </div>
                        {errorList.length > 0 && (
                            <ul className="ml-10 space-y-2">
                                {errorList.map((error, index) => (
                                    <li key={index} className="text-sm text-red-700">
                                        • {error.message}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <StepNavigation
                onPrevious={onPrevious}
                onNext={handleSubmit}
                nextLabel={submitStatus === 'loading' ? 'Enviando...' : 'Finalizar'}
                isLoading={submitStatus === 'loading'}
                nextDisabled={!canContinue || submitStatus === 'loading'}
            />
        </StepContainer>
    );
};

export default Step22_RegraFinal;
